import React, { useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { StyledSelect } from './SharedComponents';
import { SavedRule, MappingRow } from './types';
import { getRiskColor } from './helpers';
import { initialHandlerOptions } from './constants';

export interface ApprovalRuleSectionProps {
    savedRules?: SavedRule[];
    selectedRule?: SavedRule;
    selectedRuleId?: number;
    onSelectRuleId?: (ruleId: number) => void;
    onChangeRule?: (ruleId: number) => void;
    onNavigateToTab?: (tabIdx: number) => void;
    mappings?: MappingRow[];
}

const ApprovalRuleSection: React.FC<ApprovalRuleSectionProps> = ({
    savedRules = [],
    selectedRule,
    selectedRuleId,
    onSelectRuleId,
    onChangeRule,
    onNavigateToTab,
    mappings = []
}) => {
    // Determine active rule from selectedRuleId, selectedRule prop, or fallback to latest savedRule
    const activeRule = useMemo(() => {
        if (selectedRuleId !== undefined && savedRules.length > 0) {
            const found = savedRules.find((r) => r.id === selectedRuleId);
            if (found) return found;
        }
        if (selectedRule) return selectedRule;
        if (savedRules.length > 0) return savedRules[savedRules.length - 1];
        return undefined;
    }, [savedRules, selectedRule, selectedRuleId]);

    const activeMapping = useMemo(() => {
        if (!activeRule) return undefined;
        return mappings.find((m) =>
            m.ewsRule.toLowerCase() === activeRule.ruleName.toLowerCase() ||
            m.id === activeRule.id
        );
    }, [mappings, activeRule]);

    const displayedRuleName = activeRule?.ruleName || 'EWS Rule';
    const rawStatus = (activeRule?.status || 'configured').toLowerCase();
    const displayedStatus = rawStatus === 'pending_approval' ? 'pending review' : rawStatus;
    const displayedRiskLevel = (activeRule?.riskLevel || 'medium').toUpperCase();
    const displayedSignal = activeRule?.ewsSignalName || 'EWS.Volume.Drift';
    const displayedDomainPath = activeRule
        ? `${activeRule.domain || activeRule.metric || 'Volume'} > ${activeRule.component || activeRule.dataSourceLabel || 'Ingestion'} > ${activeRule.layer || 'Bronze / All'}`
        : 'Volume > Ingestion > Bronze / All';

    const displayedHandler = useMemo(() => {
        if (activeMapping?.handler) {
            const matchedOpt = initialHandlerOptions.find((o) => o.value.toLowerCase() === activeMapping.handler.toLowerCase());
            return matchedOpt ? matchedOpt.label : activeMapping.handler;
        }
        if (activeRule?.handler) return activeRule.handler;
        return 'Pepwise DB';
    }, [activeMapping, activeRule]);

    const handleRuleSelectChange = (idStr: string) => {
        const numId = Number(idStr);
        onSelectRuleId?.(numId);
    };

    const handleChangeRuleClick = () => {
        const targetId = activeRule?.id || selectedRuleId;
        if (onChangeRule && targetId != null) {
            onChangeRule(targetId);
        } else if (onNavigateToTab) {
            onNavigateToTab(0);
        }
    };

    return (
        <Box
            sx={{
                border: '1.5px solid #39ACE7',
                borderRadius: '8px',
                backgroundColor: '#f4f9fd',
                p: 2.5,
                mb: 3,
                boxShadow: '0 2px 6px rgba(0, 55, 113, 0.06)'
            }}
        >
            {/* Top Row: Rule Selection / Title + Status Chip + Risk Badge + Change Rule Button */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {savedRules.length > 1 ? (
                        <Box sx={{ minWidth: 220, maxWidth: 320 }}>
                            <StyledSelect
                                label="Selected Rule"
                                hideLabel
                                value={String(activeRule?.id || '')}
                                onChange={handleRuleSelectChange}
                                options={savedRules.map((r) => ({
                                    value: String(r.id),
                                    label: r.ruleName
                                }))}
                                height={34}
                            />
                        </Box>
                    ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#39ACE7' }}>
                            {displayedRuleName}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            border: '1px solid #94a3b8',
                            color: '#64748b',
                            backgroundColor: '#fff',
                            px: 1.2,
                            py: 0.2,
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'lowercase'
                        }}
                    >
                        {displayedStatus}
                    </Box>

                    <Box
                        sx={{
                            backgroundColor: getRiskColor(displayedRiskLevel),
                            color: '#fff',
                            px: 1.5,
                            py: 0.2,
                            borderRadius: '12px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            textTransform: 'uppercase'
                        }}
                    >
                        {displayedRiskLevel}
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleChangeRuleClick}
                    endIcon={<EditIcon sx={{ fontSize: '0.95rem !important' }} />}
                    sx={{
                        borderColor: '#39ACE7',
                        color: '#39ACE7',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.75,
                        py: 0.4,
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        '&:hover': {
                            borderColor: '#2c8ebb',
                            backgroundColor: '#f0f9ff'
                        }
                    }}
                >
                    Change rule
                </Button>
            </Box>

            {/* Bottom Row: 3 Columns (Signal, Domain path, Handler) */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1.5fr 1fr' },
                    gap: 2,
                    pt: 1.5,
                    borderTop: '1px solid #e0edf7'
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                        Signal
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {displayedSignal}
                    </Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                        Domain path
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {displayedDomainPath}
                    </Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                        Handler
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {displayedHandler}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default ApprovalRuleSection;
